import {
  DatePickerV2,
  TextV2,
  ButtonV2,
  SvgMoveDown1Regular,
  Link,
} from '@insights-ltd/design-library/components';
import { isValidPastDateRange } from 'components/EventForms/validation';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { toDownloadRangeFormatFromDateTime } from 'utils/dates';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import { useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import downloadTransactionHistory from './downloadTransactionHistory';

type DateTimeState = DateTime | null;

const datePickerWidth = '329px';
const TRANSACTION_EXPORT_LIMIT_DAYS = 396;
const INITIAL_DATE_RANGE_DIFFERENCE = 90;

export const ExportTransactions = ({
  practitionerId,
}: {
  practitionerId: string;
}) => {
  const [startDate, setStartDate] = useState<DateTimeState>(
    DateTime.now().minus({ days: INITIAL_DATE_RANGE_DIFFERENCE }),
  );
  const [isLoading, setLoading] = useState(false);
  const [endDate, setEndDate] = useState<DateTimeState>(DateTime.now());

  const handleClearDateRange = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setStartDate(null);
    setEndDate(null);
  };

  const exportDisabled = !isValidPastDateRange(
    startDate,
    endDate,
    TRANSACTION_EXPORT_LIMIT_DAYS,
  );
  const { openErrorModal } = useRequestErrorContext();

  const handleExport = () => {
    setLoading(true);
    downloadTransactionHistory({
      externalId: practitionerId,
      from: toDownloadRangeFormatFromDateTime(startDate || undefined),
      to: toDownloadRangeFormatFromDateTime(endDate || undefined),
    })
      .catch(() => openErrorModal())
      .finally(() => setLoading(false));
  };

  const theme = useTheme();

  return (
    <Grid
      container
      direction="row"
      spacing={1}
      sx={{
        width: '100%',
        marginLeft: '0',
        paddingRight: 2,
        marginBottom: 1,
        marginTop: 2,
        padding: 2,
        border: `1px solid ${theme.palette.grey[500]}`,
        borderRadius: '4px',
        alignItems: 'center',
      }}
    >
      <Grid
        sx={{
          paddingLeft: 2,
          paddingTop: 2,
          paddingBottom: 0,
        }}
      >
        <DatePickerV2
          data-testid="start-date-picker"
          value={startDate}
          onChange={(event: DateTimeState) => {
            setStartDate(event);
          }}
          sx={{ width: datePickerWidth }}
          labelText={t('ui.event-management.events.create.label.start-date')}
          disableFuture
          maxDate={endDate || DateTime.local()}
        />
      </Grid>
      <Grid
        size={{ md: 'auto', xs: 12 }}
        sx={{
          alignContent: 'flex-end',
          paddingLeft: 2,
          paddingTop: 7,
          paddingBottom: 1,
        }}
      >
        <TextV2>{t('ui.event-management.events.create.label.to')}</TextV2>
      </Grid>
      <Grid
        size={{ md: 'auto', xs: 12 }}
        sx={{
          paddingLeft: 2,
          paddingTop: 2,
          paddingBottom: 0,
        }}
      >
        <DatePickerV2
          value={endDate}
          onChange={(event: DateTimeState) => setEndDate(event)}
          sx={{ width: datePickerWidth }}
          labelText={t('ui.event-management.events.create.label.end-date')}
          disableFuture
        />
      </Grid>
      <Grid
        size="grow"
        sx={{
          paddingLeft: 2,
          paddingTop: 7,
          paddingRight: 0,
          paddingBottom: 1,
        }}
      >
        <Link
          id="clear"
          sx={{ whiteSpace: 'pre' }}
          onClick={handleClearDateRange}
          data-testid="clear"
          href={''}
        >
          {t('ui.event-management.group.add-remove-organisation.dialog.clear')}
        </Link>
      </Grid>
      <Grid
        size={{ xs: 12, md: 'grow' }}
        sx={{
          paddingLeft: 0,
          paddingTop: 3,
          paddingRight: 0,
          paddingBottom: 1,
          textAlign: { md: 'right' },
        }}
      >
        <ButtonV2
          disabled={exportDisabled || isLoading}
          buttonIcon={<SvgMoveDown1Regular />}
          buttonText={t(
            'ui.event-management.my-account.transactions.export_numbered_transactions',
          )}
          style={{ whiteSpace: 'pre' }}
          onClick={handleExport}
          data-testid="export-button"
        />
      </Grid>
      <Grid
        size={12}
        sx={{
          paddingLeft: 2,
          paddingTop: 1,
          paddingBottom: 0,
        }}
      >
        <TextV2 variant="bodySmall">
          {t('ui.event-management.my-account.transactions.export_limit')}
        </TextV2>
      </Grid>
    </Grid>
  );
};
